import React, { useState } from 'react';
import InboxOutlined from '@ant-design/icons/InboxOutlined';
import { Form, Input, Progress, Upload } from 'antd';
import type { RcFile, UploadFile, UploadProps } from 'antd/lib/upload/interface';
import type { Dispatch } from 'dva';

import { apiPaths } from '../../config/api-paths.js';
import type { VersionFormValue } from '../../types/domain.js';
import { alertSuccess } from '../../utils/edittools.js';
import { readAsBinaryString } from '../../utils/utils.js';

const { Dragger } = Upload;
const { TextArea } = Input;

interface UploadResponse {
  readonly code: number;
  readonly message: string;
}

interface ReleaseFile extends RcFile {
  hash?: string;
  path?: string;
  status?: UploadFile['status'];
}

export interface VersionUploadFormProps {
  readonly mode: string;
  readonly namespace: 'upload' | 'pltUpload';
  readonly appVersion: 'pclab2' | 'plt' | 'hxrInstaller';
  readonly versionData: Readonly<Record<string, string>>;
  readonly dataValues: Readonly<Record<string, VersionFormValue>>;
  readonly validExtensions: readonly string[];
  readonly accept: string;
  readonly changelogLabel?: string;
  readonly uploadHint?: React.ReactNode;
  readonly dispatch: Dispatch;
}

function extension(name: string): string {
  return name.substring(name.lastIndexOf('.') + 1);
}

export default function VersionUploadForm({
  mode,
  namespace,
  appVersion,
  versionData,
  dataValues,
  validExtensions,
  accept,
  changelogLabel = '更新日志',
  uploadHint = '支持单文件上传',
  dispatch,
}: VersionUploadFormProps): React.ReactElement {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [percent, setPercent] = useState(0);
  const fields = dataValues[mode] ?? {};

  const setStatus = (fileName: string, status: NonNullable<UploadFile['status']>): void => {
    setFileList((currentList) => {
      const nextList = currentList.map((file) =>
        file.name === fileName ? { ...file, status } : file,
      );
      const completed = nextList.filter(
        (file) => file.status && file.status !== 'uploading',
      ).length;
      setPercent(nextList.length ? Number(((completed / nextList.length) * 100).toFixed(2)) : 0);
      return nextList;
    });
  };

  const uploadFetch = async (path: string, body: FormData, fileName: string): Promise<void> => {
    const csrfToken = document.cookie.match(/csrfToken=([^;]+)/u)?.[1];
    const headers: HeadersInit = csrfToken ? { 'x-csrf-token': csrfToken } : {};
    try {
      const response = await fetch(path, {
        method: 'POST',
        body,
        headers,
        credentials: 'same-origin',
      });
      const result = (await response.json()) as UploadResponse;
      if (result.code !== 0) {
        window.alertError(result.message);
        setStatus(fileName, 'error');
        return;
      }
      setFileList([]);
      setStatus(fileName, 'done');
    } catch (error) {
      console.error(error);
      window.alertError(error instanceof Error ? error.message : error);
      setStatus(fileName, 'error');
    }
  };

  const onUpload = async (file: ReleaseFile): Promise<void> => {
    const { version, changelog } = fields;
    const body = new FormData();
    body.append('file', file);
    const hash = await readAsBinaryString(file);
    const query = `size=${file.size}&filePath=${file.path ?? ''}&hash=${hash}&version=${version}&mode=${mode}&changelog=${encodeURI(changelog ?? '')}&appVersion=${appVersion}`;
    await uploadFetch(`${apiPaths.pclabFileUpload}?${query}`, body, file.name);
    alertSuccess(`版本 ${version} 发布成功`);
    dispatch({
      type: `${namespace}/update`,
      payload: { dataValues: { ...dataValues, [mode]: { ...fields, version, changelog: '' } } },
    });
  };

  const beforeUpload = async (rawFile: RcFile, rawList: RcFile[]): Promise<boolean> => {
    const file = rawFile as ReleaseFile;
    const { version, changelog } = fields;
    if (!version) {
      window.alertError('请输入版本号');
      throw new Error('请输入版本号');
    }
    if (!changelog) {
      window.alertError('请输入更新日志');
      throw new Error('请输入更新日志');
    }
    const extName = extension(file.name);
    if (!extName) {
      file.status = 'error';
      throw new Error(`文件 ${file.name} 获取后缀名失败`);
    }
    if (/^[._]/u.test(file.name)) {
      file.status = 'error';
      throw new Error(`${file.name} 是隐藏文件，这个文件将被忽略！`);
    }
    if (!validExtensions.includes(extName)) {
      file.status = 'error';
      window.alertError(`${file.name} 不是${validExtensions.join('或')}文件`);
      throw new Error(`${file.name} 不是${validExtensions.join('或')}文件`);
    }
    file.hash = await readAsBinaryString(file);
    const paths = file.webkitRelativePath.split('/');
    file.path = paths.slice(1, paths.length - 1).join('/');
    file.status = 'done';
    setFileList(rawList.map((entry) => entry as UploadFile));
    return true;
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept,
    beforeUpload,
    customRequest: (info) => {
      void onUpload(info.file as ReleaseFile);
    },
  };

  return (
    <Form
      name={`upload-${mode}`}
      form={form}
      labelCol={{ span: namespace === 'upload' ? 2 : 3 }}
      wrapperCol={{ span: namespace === 'upload' ? 22 : 19 }}
      initialValues={fields}
      onValuesChange={(_changed, values: VersionFormValue) => {
        dispatch({
          type: `${namespace}/update`,
          payload: { dataValues: { ...dataValues, [mode]: { ...fields, ...values, mode } } },
        });
      }}
      autoComplete="off"
    >
      <Form.Item
        label="版本号"
        name="version"
        rules={[{ required: true, message: 'Please input your version!' }]}
      >
        <Input placeholder={versionData[mode] ? `上次版本为${versionData[mode]}` : ''} />
      </Form.Item>
      <Form.Item
        label={changelogLabel}
        name="changelog"
        rules={[{ required: true, message: 'Please input changelog!' }]}
      >
        <TextArea autoSize={{ minRows: 2 }} />
      </Form.Item>
      <Form.Item
        label="上传文件"
        name="file"
        rules={[{ required: true, message: 'Please input changelog!' }]}
      >
        <Dragger {...uploadProps} fileList={fileList}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {namespace === 'upload'
              ? 'Click or drag file to this area to upload'
              : '点击此区域上传'}
          </p>
          <p className="ant-upload-hint">{uploadHint}</p>
        </Dragger>
        {fileList.length ? <Progress percent={percent} /> : null}
      </Form.Item>
    </Form>
  );
}
